import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import HomePage from './pages/HomePage';
import TypesListPage from './pages/TypesListPage';
import TypeDetailPage from './pages/TypeDetailPage';
import DiagnosisV2Page from './pages/DiagnosisV2Page';
import ResultV2Page from './pages/ResultV2Page';
import DiagnosisDynamicPage from './pages/DiagnosisDynamicPage';
import DynamicResultPage from './pages/DynamicResultPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import MyDiagnosesPage from './pages/MyDiagnosesPage';
import ChatPage from './pages/ChatPage';
import InsightPage from './pages/InsightPage';
import CompanyLandingPage from './pages/company/CompanyLandingPage';
import CompanyCreatePage from './pages/company/CompanyCreatePage';
import CompanyJoinPage from './pages/company/CompanyJoinPage';
import CompanyDashboard from './pages/company/CompanyDashboard';
import TeamDetailPage from './pages/company/TeamDetailPage';
import MemberManagePage from './pages/company/MemberManagePage';
import MemberDetailPage from './pages/company/MemberDetailPage';
import CompanySettingsPage from './pages/company/CompanySettingsPage';
import DepartmentDetailPage from './pages/company/DepartmentDetailPage';
import PositionDetailPage from './pages/company/PositionDetailPage';
import AiReportPage from './pages/company/AiReportPage';
import ReportPage from './pages/ReportPage';
import AccountPage from './pages/AccountPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SuperAdminPage from './pages/admin/SuperAdminPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUserDetailPage from './pages/admin/AdminUserDetailPage';
import AdminBillingPage from './pages/admin/AdminBillingPage';
import AdminUsagePage from './pages/admin/AdminUsagePage';
import AdminRoute from './components/AdminRoute';
import TokushohoPage from './pages/legal/TokushohoPage';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import nightTheme from './theme/tokens';
import useSeo from './seo/useSeo';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/** ルート遷移に追従して title / description / canonical / OGP を更新する */
function SeoHead() {
  useSeo();
  return null;
}

// v2.0 Night Horoscope theme — tokens defined in ./theme/tokens.js
const theme = nightTheme;

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.bgPrimary};
  color: ${props => props.theme.colors.text};
`;

const MainContent = styled.main`
  flex: 1;
`;

function AppContent() {
  const { pathname } = useLocation();
  const hideFooter = pathname === '/chat';

  return (
    <AppContainer>
      <SeoHead />
      <Header />
      <MainContent>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/types" element={<TypesListPage />} />
          <Route path="/types/:typeCode" element={<TypeDetailPage />} />
          <Route path="/current-diagnosis" element={<DiagnosisDynamicPage />} />
          <Route path="/current-result/:diagnosisId" element={<DynamicResultPage />} />
          <Route path="/natal-diagnosis" element={<DiagnosisV2Page />} />
          <Route path="/natal-result/:diagnosisId" element={<ResultV2Page />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/my-diagnoses" element={<MyDiagnosesPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/insight" element={<InsightPage />} />
          <Route path="/diagnosis" element={<DiagnosisDynamicPage />} />
          <Route path="/result/:diagnosisId" element={<DynamicResultPage />} />
          {/* Individual billing routes */}
          <Route path="/report/:reportType" element={<ReportPage />} />
          <Route path="/account" element={<AccountPage />} />
          {/* Legal routes */}
          <Route path="/legal/tokushoho" element={<TokushohoPage />} />
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          {/* Admin routes (protected) */}
          <Route path="/admin" element={<AdminRoute><SuperAdminPage /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          <Route path="/admin/users/:userId" element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} />
          <Route path="/admin/billing" element={<AdminRoute><AdminBillingPage /></AdminRoute>} />
          <Route path="/admin/usage" element={<AdminRoute><AdminUsagePage /></AdminRoute>} />
          {/* BtoB Company routes */}
          <Route path="/company" element={<CompanyLandingPage />} />
          <Route path="/company/create" element={<CompanyCreatePage />} />
          <Route path="/company/join" element={<CompanyJoinPage />} />
          <Route path="/company/:slug" element={<CompanyDashboard />} />
          <Route path="/company/:slug/members" element={<MemberManagePage />} />
          <Route path="/company/:slug/members/:memberId" element={<MemberDetailPage />} />
          <Route path="/company/:slug/ai-report" element={<AiReportPage />} />
          <Route path="/company/:slug/departments/:deptId" element={<DepartmentDetailPage />} />
          <Route path="/company/:slug/teams/:teamId" element={<TeamDetailPage />} />
          <Route path="/company/:slug/positions/:position" element={<PositionDetailPage />} />
          <Route path="/company/:slug/settings" element={<CompanySettingsPage />} />
        </Routes>
      </MainContent>
      {!hideFooter && <Footer />}
    </AppContainer>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <LanguageProvider>
        <AuthProvider>
          <ChatProvider>
            <ScrollToTop />
            <AppContent />
          </ChatProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
